/**
 * React Hooks for Complex Process Data
 * 
 * Provides easy-to-use hooks for components to interact with process data
 */

import { useCallback, useEffect, useState } from 'react';
import { complexProcessRepository } from '../repositories/ComplexProcessRepository';
import {
    ComplexProcessDefinition,
    ProcessConnection,
    ProcessFilterOptions,
    ProcessSortOptions,
    ProcessStage,
    ProcessValidationResult
} from '../types/ComplexProcess';

// Hook for fetching all processes
export function useProcesses(options?: {
    filter?: ProcessFilterOptions;
    sort?: ProcessSortOptions;
    autoRefresh?: boolean;
    refreshInterval?: number;
}) {
    const [processes, setProcesses] = useState<ComplexProcessDefinition[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchProcesses = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const data = await complexProcessRepository.findAll({
                filter: options?.filter as any,
                sort: options?.sort as any
            });
            setProcesses(data);
        } catch (err) {
            setError(err as Error);
            setProcesses([]);
        } finally {
            setLoading(false);
        }
    }, [options?.filter, options?.sort]);

    useEffect(() => {
        fetchProcesses();

        if (options?.autoRefresh && options?.refreshInterval) {
            const interval = setInterval(fetchProcesses, options.refreshInterval);
            return () => clearInterval(interval);
        }
        return undefined;
    }, [fetchProcesses, options?.autoRefresh, options?.refreshInterval]);

    return {
        processes,
        loading,
        error,
        refetch: fetchProcesses
    };
}

// Hook for fetching a single process
export function useProcess(processId: string | null) {
    const [process, setProcess] = useState<ComplexProcessDefinition | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const fetchProcess = useCallback(async () => {
        if (!processId) {
            setProcess(null);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const data = await complexProcessRepository.findById(processId);
            setProcess(data);
        } catch (err) {
            setError(err as Error);
            setProcess(null);
        } finally {
            setLoading(false);
        }
    }, [processId]);

    useEffect(() => {
        fetchProcess();
    }, [fetchProcess]);

    return {
        process,
        loading,
        error,
        refetch: fetchProcess
    };
}

// Hook for process CRUD operations
export function useProcessMutations() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const createProcess = useCallback(async (data: Partial<ComplexProcessDefinition>) => {
        setLoading(true);
        setError(null);

        try {
            const result = await complexProcessRepository.create(data);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const updateProcess = useCallback(async (id: string, data: Partial<ComplexProcessDefinition>) => {
        setLoading(true);
        setError(null);

        try {
            const result = await complexProcessRepository.update(id, data);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const deleteProcess = useCallback(async (id: string) => {
        setLoading(true);
        setError(null);

        try {
            const result = await complexProcessRepository.delete(id);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const cloneTemplate = useCallback(async (templateId: string, newName: string) => {
        setLoading(true);
        setError(null);

        try {
            const result = await complexProcessRepository.cloneTemplate(templateId, newName);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        createProcess,
        updateProcess,
        deleteProcess,
        cloneTemplate,
        loading,
        error
    };
}

// Hook for process stage operations
export function useProcessStages(processId: string | null) {
    const { process, loading: processLoading, error: processError, refetch } = useProcess(processId);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const addStage = useCallback(async (stage: ProcessStage): Promise<unknown | null> => {
        if (!processId) return null;

        setLoading(true);
        setError(null);

        try {
            const result = await complexProcessRepository.addStage(processId, stage);
            await refetch();
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [processId, refetch]);

    const removeStage = useCallback(async (stageId: string): Promise<unknown | null> => {
        if (!processId) return null;

        setLoading(true);
        setError(null);

        try {
            const result = await complexProcessRepository.removeStage(processId, stageId);
            await refetch();
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [processId, refetch]);

    const updateStage = useCallback(async (stageId: string, updates: Partial<ProcessStage>): Promise<unknown | null> => {
        if (!processId || !process) return null;

        setLoading(true);
        setError(null);

        try {
            const updatedStages = process.stages.map(s =>
                s.id === stageId ? { ...s, ...updates } : s
            );

            const result = await complexProcessRepository.update(processId, { stages: updatedStages });
            await refetch();
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [processId, process, refetch]);

    return {
        stages: process?.stages || [],
        addStage,
        removeStage,
        updateStage,
        loading: processLoading || loading,
        error: processError || error
    };
}

// Hook for process connection operations
export function useProcessConnections(processId: string | null) {
    const { process, loading: processLoading, error: processError, refetch } = useProcess(processId);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const addConnection = useCallback(async (connection: ProcessConnection): Promise<unknown | null> => {
        if (!processId) return null;

        setLoading(true);
        setError(null);

        try {
            const result = await complexProcessRepository.addConnection(processId, connection);
            await refetch();
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [processId, refetch]);

    const removeConnection = useCallback(async (connectionId: string): Promise<unknown | null> => {
        if (!processId || !process) return null;

        setLoading(true);
        setError(null);

        try {
            const updatedConnections = process.connections.filter(c => c.id !== connectionId);
            const result = await complexProcessRepository.update(processId, {
                connections: updatedConnections
            });
            await refetch();
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [processId, process, refetch]);

    return {
        connections: process?.connections || [],
        addConnection,
        removeConnection,
        loading: processLoading || loading,
        error: processError || error
    };
}

// Hook for process search
export function useProcessSearch(query: string, debounceMs: number = 300) {
    const [results, setResults] = useState<ComplexProcessDefinition[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        let cleanup: (() => void) | undefined;
        if (!query || query.trim() === '') {
            setResults([]);
        } else {
            const timer = setTimeout(async () => {
                setLoading(true);
                setError(null);

                try {
                    const data = await complexProcessRepository.search(query);
                    setResults(data);
                } catch (err) {
                    setError(err as Error);
                    setResults([]);
                } finally {
                    setLoading(false);
                }
            }, debounceMs);
            cleanup = () => clearTimeout(timer);
        }
        return cleanup;
    }, [query, debounceMs]);

    return {
        results,
        loading,
        error
    };
}

// Hook for process templates
export function useProcessTemplates(domain?: string) {
    const [templates, setTemplates] = useState<ComplexProcessDefinition[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const fetchTemplates = async () => {
            setLoading(true);
            setError(null);

            try {
                const data = domain
                    ? await complexProcessRepository.findByDomain(domain)
                    : await complexProcessRepository.findTemplates();

                const templatesOnly = data.filter(p => p.isTemplate);
                setTemplates(templatesOnly);
            } catch (err) {
                setError(err as Error);
                setTemplates([]);
            } finally {
                setLoading(false);
            }
        };

        fetchTemplates();
    }, [domain]);

    return {
        templates,
        loading,
        error
    };
}

// Hook for process statistics
export function useProcessStatistics() {
    const [statistics, setStatistics] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const fetchStatistics = async () => {
            setLoading(true);
            setError(null);

            try {
                const data = await complexProcessRepository.getStatistics();
                setStatistics(data);
            } catch (err) {
                setError(err as Error);
                setStatistics(null);
            } finally {
                setLoading(false);
            }
        };

        fetchStatistics();
    }, []);

    return {
        statistics,
        loading,
        error
    };
}

// Hook for process validation
export function useProcessValidation(process: ComplexProcessDefinition | null) {
    const [validation, setValidation] = useState<ProcessValidationResult | null>(null);

    useEffect(() => {
        if (!process) {
            setValidation(null);
            return;
        }

        // Client-side validation
        const errors: any[] = [];
        const warnings: any[] = [];
        const suggestions: any[] = [];

        // Basic validation
        if (!process.name || process.name.trim() === '') {
            errors.push({ field: 'name', message: 'Process name is required', severity: 'error' });
        }

        if (process.stages.length === 0) {
            errors.push({ field: 'stages', message: 'Process must have at least one stage', severity: 'error' });
        }

        // Check for entry and exit stages
        const hasEntry = process.stages.some(s => s.stageType === 'Entry');
        const hasExit = process.stages.some(s => s.stageType === 'Exit');

        if (!hasEntry) {
            warnings.push({ field: 'stages', message: 'Process should have an Entry stage' });
        }

        if (!hasExit) {
            warnings.push({ field: 'stages', message: 'Process should have an Exit stage' });
        }

        // Check for disconnected stages
        const connectedStages = new Set<string>();
        process.connections.forEach(conn => {
            connectedStages.add(conn.from);
            connectedStages.add(conn.to);
        });

        process.stages.forEach(stage => {
            if (!connectedStages.has(stage.id) && process.stages.length > 1) {
                warnings.push({
                    field: 'connections',
                    message: `Stage "${stage.name}" is not connected to other stages`
                });
            }
        });

        // Suggestions
        if (process.stages.length > 20) {
            suggestions.push({
                type: 'complexity',
                message: 'Consider breaking this process into smaller sub-processes',
                action: 'split'
            });
        }

        if (process.actors.length > 10) {
            suggestions.push({
                type: 'actors',
                message: 'Many actors involved. Consider using roles instead of individuals',
                action: 'simplify'
            });
        }

        setValidation({
            valid: errors.length === 0,
            errors,
            warnings,
            suggestions
        });
    }, [process]);

    return validation;
}

export default {
    useProcesses,
    useProcess,
    useProcessMutations,
    useProcessStages,
    useProcessConnections,
    useProcessSearch,
    useProcessTemplates,
    useProcessStatistics,
    useProcessValidation
};
